import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconArrowBackUp, IconEraser, IconEye, IconEyeOff, IconPlus, IconRefresh } from '@tabler/icons-react';
import {
    OBJECT_KINDS, cloneObjectSettings, hasObjectChange, kindHasChange,
    runObjectDetect, renderObjectAdjust, renderObjectAdjustBlob, renderMaskOverlay,
    paintMaskEdit, beginMaskEditStroke, undoMaskEdit, canUndoMaskEdit,
} from './objectMaskEngine';
import { notifications } from '../../../lib/notifications';

// Live preview is rendered at most this wide/tall. The photo frame is ~520px
// tall on screen, so anything larger is invisible detail that only costs
// encode time on every slider move.
const PREVIEW_MAX = 1200;

const ACCENT = '#fe9a00';

/**
 * Inline Face / Skin / Hair panel for the editor sidebar.
 *
 * Detection and all pixel work happen in the browser. While the panel is open it
 * streams a preview image up to the parent (`onPreview`), which shows it in the
 * main photo frame in place of the working image; Apply hands over the
 * full-resolution PNG.
 */
export default function ObjectAdjustPanel({ imageSrc, onPreview, onBrush, onApply, onClose, t }) {
    const [status, setStatus] = useState('download'); // download | detect | idle | applying
    const [kind, setKind] = useState('face');
    const [settings, setSettings] = useState(() => cloneObjectSettings());
    const [showMask, setShowMask] = useState(false);
    const [found, setFound] = useState(null);
    const [failed, setFailed] = useState(null);
    // Brush over the detected selection: null = off, otherwise 'add' | 'erase'.
    const [brush, setBrush] = useState(null);
    // % of the photo's long edge. Small by default: the brush is for correcting
    // an edge the parser missed, and a wide one swallows the detail being fixed.
    const [brushSize, setBrushSize] = useState(4);
    const [canUndo, setCanUndo] = useState(false);

    const parseRef = useRef(null);
    const frameRef = useRef(0);
    const renderingRef = useRef(false);
    const pendingRef = useRef(false);
    const urlRef = useRef(null);

    const busy = status === 'download' || status === 'detect' || status === 'applying';
    const ready = !!parseRef.current;

    const publish = useCallback((url) => {
        const old = urlRef.current;
        urlRef.current = url;
        onPreview(url);
        if (old) setTimeout(() => URL.revokeObjectURL(old), 0);
    }, [onPreview]);

    // ── Preview render → parent ───────────────────────────────────────────────
    const paint = useCallback(() => {
        const parse = parseRef.current;
        if (!parse) return;
        // Coalesce bursts of slider events: at most one render in flight, with a
        // single queued follow-up so the last value always wins.
        if (renderingRef.current) { pendingRef.current = true; return; }
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(() => {
            renderingRef.current = true;
            const scale = Math.min(1, PREVIEW_MAX / Math.max(parse.imgW, parse.imgH));
            const w = Math.max(1, Math.round(parse.imgW * scale));
            const h = Math.max(1, Math.round(parse.imgH * scale));
            const canvas = renderObjectAdjust(parse, settings, w, h);
            if (showMask) canvas.getContext('2d').drawImage(renderMaskOverlay(parse, kind, w, h), 0, 0);
            canvas.toBlob((blob) => {
                renderingRef.current = false;
                if (blob) publish(URL.createObjectURL(blob));
                if (pendingRef.current) { pendingRef.current = false; paint(); }
            }, 'image/png');
        });
    }, [settings, showMask, kind, publish]);

    useEffect(() => { if (parseRef.current) paint(); }, [paint]);

    // ── Brush → parent ────────────────────────────────────────────────────────
    // The photo lives in the editor's own frame, so the parent draws the capture
    // surface; this hands it the stroke handler and the cursor size to show.
    // Held in a ref so the object handed to the parent can stay identity-stable:
    // re-publishing it on every stroke unmounts the parent's capture surface
    // mid-drag, and the pointer capture goes with it.
    const strokeRef = useRef(null);
    strokeRef.current = (stroke) => {
        const parse = parseRef.current;
        if (!parse || !brush) return;
        const changed = paintMaskEdit(parse, kind, {
            ...stroke,
            radius: (brushSize / 100) / 2,
            mode: brush,
        });
        if (!changed) return;
        setCanUndo(true);
        paint();
    };

    // Called once per gesture, on pointer-down, so one Undo takes back one
    // stroke rather than the whole session's painting.
    const strokeStartRef = useRef(null);
    strokeStartRef.current = () => beginMaskEditStroke(parseRef.current, kind);

    useEffect(() => {
        if (!onBrush) return undefined;
        onBrush(brush
            ? {
                mode: brush,
                size: brushSize / 100,
                onStrokeStart: () => strokeStartRef.current?.(),
                onStroke: (s) => strokeRef.current?.(s),
            }
            : null);
        return () => onBrush(null);
    }, [brush, brushSize, onBrush]);

    // Painting blind makes no sense — turning the brush on shows the overlay.
    useEffect(() => { if (brush) setShowMask(true); }, [brush]);

    // History is per object, so Undo's enabled state follows the tab.
    useEffect(() => { setCanUndo(canUndoMaskEdit(parseRef.current, kind)); }, [kind]);

    // ── Detect once, on mount ─────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const parse = await runObjectDetect(imageSrc, (stage) => {
                    if (!cancelled) setStatus(stage === 'detect' ? 'detect' : 'download');
                });
                if (cancelled) return;
                parseRef.current = parse;
                setFound(parse.found);
                setKind(OBJECT_KINDS.find((k) => parse.found[k]) || 'face');
                setStatus('idle');
            } catch (err) {
                if (cancelled) return;
                console.error('[ObjectAdjust] detect failed:', err);
                setFailed(err?.message || String(err));
                setStatus('idle');
                notifications.show({ message: t('objectAdjust.error'), color: 'red' });
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageSrc]);

    // Drop the preview (and its object URL) whenever the panel goes away.
    useEffect(() => () => {
        cancelAnimationFrame(frameRef.current);
        const url = urlRef.current;
        urlRef.current = null;
        onPreview(null);
        if (url) URL.revokeObjectURL(url);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = (patch) => setSettings((s) => ({ ...s, [kind]: { ...s[kind], ...patch } }));

    const handleApply = async () => {
        const parse = parseRef.current;
        if (!parse || busy) return;
        if (!hasObjectChange(settings)) { notifications.show({ message: t('objectAdjust.noChange'), color: 'red' }); return; }
        setStatus('applying');
        try {
            const blob = await renderObjectAdjustBlob(parse, settings);
            onApply(blob);
            notifications.show({ message: t('objectAdjust.done'), color: 'green' });
            onClose();
        } catch (err) {
            console.error('[ObjectAdjust] apply failed:', err);
            notifications.show({ message: t('objectAdjust.error'), color: 'red' });
            setStatus('idle');
        }
    };

    const cur = settings[kind];

    if (failed) {
        return (
            <div style={box}>
                <div style={{ fontSize: 11, color: '#dc2626', lineHeight: 1.5 }}>{t('objectAdjust.error')}</div>
                <code style={{ fontSize: 9, color: '#9ca3af', wordBreak: 'break-all', lineHeight: 1.4 }}>{failed}</code>
                <button onClick={onClose} style={btnGhost}>{t('common.cancel')}</button>
            </div>
        );
    }

    if (!ready) {
        return (
            <div style={{ ...box, alignItems: 'center', flexDirection: 'row', gap: 8 }}>
                <span style={spinner} />
                <span style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>
                    {status === 'download' ? t('objectAdjust.loadingModel') : t('objectAdjust.detecting')}
                </span>
            </div>
        );
    }

    return (
        <div style={box}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                {OBJECT_KINDS.map((k) => {
                    const active = kind === k;
                    const off = found && !found[k];
                    return (
                        <button
                            key={k}
                            onClick={() => setKind(k)}
                            title={off ? t('objectAdjust.notFound') : undefined}
                            style={{
                                padding: '5px 2px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                                border: `1.5px solid ${active ? ACCENT : 'var(--color-border, #e5e7eb)'}`,
                                background: active ? '#fff7ed' : 'var(--color-surface, #fff)',
                                color: active ? ACCENT : (off ? '#c4c8ce' : '#6b7280'),
                                textDecoration: off ? 'line-through' : 'none',
                            }}
                        >
                            {t(`objectAdjust.${k}`)}
                            {kindHasChange(settings, k) && <span style={{ marginLeft: 3, fontSize: 9, verticalAlign: 'top' }}>●</span>}
                        </button>
                    );
                })}
            </div>

            {kind === 'hair' ? (
                <Slider label={t('objectAdjust.darken')} value={cur.darken} min={0} max={100}
                    onChange={(v) => update({ darken: v })} />
            ) : kind === 'shadow' ? (
                <Slider label={t('objectAdjust.lift')} value={cur.lift} min={0} max={100}
                    onChange={(v) => update({ lift: v })} />
            ) : (
                <>
                    <Slider label={t('objectAdjust.brightness')} value={cur.brightness} min={0} max={200}
                        onChange={(v) => update({ brightness: v })} />
                    <Slider label={t('objectAdjust.contrast')} value={cur.contrast} min={0} max={200}
                        onChange={(v) => update({ contrast: v })} />
                </>
            )}

            {/* ── One row of icon-only tools. Every label lives in `title`, so the
                   five controls fit a single line at the sidebar's width. ── */}
            <div style={{ borderTop: '1px dashed var(--color-border, #e5e7eb)', paddingTop: 7, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    <IconButton
                        title={t('objectAdjust.brushAdd')}
                        active={brush === 'add'}
                        onClick={() => setBrush(brush === 'add' ? null : 'add')}
                    >
                        <IconPlus size={18} stroke={2.4} />
                    </IconButton>
                    <IconButton
                        title={t('objectAdjust.brushErase')}
                        active={brush === 'erase'}
                        onClick={() => setBrush(brush === 'erase' ? null : 'erase')}
                    >
                        <IconEraser size={18} stroke={2.4} />
                    </IconButton>
                    <IconButton
                        title={t('objectAdjust.undoStroke')}
                        disabled={!canUndo}
                        onClick={() => {
                            if (!undoMaskEdit(parseRef.current, kind)) return;
                            setCanUndo(canUndoMaskEdit(parseRef.current, kind));
                            paint();
                        }}
                    >
                        <IconArrowBackUp size={18} stroke={2.4} />
                    </IconButton>
                    <IconButton
                        title={t('objectAdjust.showMask')}
                        active={showMask}
                        onClick={() => setShowMask((v) => !v)}
                    >
                        {showMask ? <IconEyeOff size={18} stroke={2.4} /> : <IconEye size={18} stroke={2.4} />}
                    </IconButton>
                    <IconButton
                        title={t('objectAdjust.reset')}
                        disabled={!hasObjectChange(settings)}
                        onClick={() => setSettings(cloneObjectSettings())}
                    >
                        <IconRefresh size={18} stroke={2.4} />
                    </IconButton>
                </div>
                {brush && (
                    <>
                        <Slider label={t('objectAdjust.brushSize')} value={brushSize} min={2} max={40}
                            onChange={setBrushSize} />
                        <div style={{ fontSize: 9.5, color: '#9ca3af', lineHeight: 1.45 }}>
                            {t('objectAdjust.brushHint')}
                        </div>
                    </>
                )}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={onClose} disabled={busy} style={{ ...btnGhost, flex: 1 }}>{t('common.cancel')}</button>
                <button
                    onClick={handleApply}
                    disabled={busy || !hasObjectChange(settings)}
                    style={{
                        flex: 1, padding: '7px 8px', borderRadius: 7, border: 'none', fontSize: 11.5, fontWeight: 700, color: '#fff',
                        background: ACCENT, opacity: (busy || !hasObjectChange(settings)) ? 0.45 : 1,
                        cursor: (busy || !hasObjectChange(settings)) ? 'not-allowed' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    }}
                >
                    {status === 'applying' && <span style={{ ...spinner, width: 11, height: 11, borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} />}
                    {t('objectAdjust.apply')}
                </button>
            </div>
        </div>
    );
}

/** Square icon-only control. The name is carried by `title` and `aria-label`. */
function IconButton({ title, active, disabled, onClick, children }) {
    return (
        <button
            type="button"
            title={title}
            aria-label={title}
            aria-pressed={active}
            disabled={disabled}
            onClick={onClick}
            style={{
                flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                height: 32, borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer',
                border: `1px solid ${active ? ACCENT : 'var(--color-border, #e5e7eb)'}`,
                background: active ? '#fff7ed' : 'transparent',
                // The old #9ca3af was too pale to read at this size — with no text
                // label beside it, the glyph is the only thing carrying meaning.
                color: active ? ACCENT : '#4b5563',
                opacity: disabled ? 0.35 : 1,
            }}
        >
            {children}
        </button>
    );
}

function Slider({ label, value, min, max, onChange }) {
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>
                <span>{label}</span>
                <span style={{ color: ACCENT }}>{value}%</span>
            </div>
            <input
                type="range" min={min} max={max} value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{ width: '100%', accentColor: ACCENT }}
            />
        </div>
    );
}

const box = {
    display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, padding: 10,
    border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 10,
    background: 'var(--color-surface, #fafafa)',
};


const btnGhost = {
    padding: '7px 8px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
    border: '1px solid var(--color-border, #e5e7eb)', background: 'transparent', color: '#6b7280',
};

const spinner = {
    width: 13, height: 13, borderRadius: '50%', flexShrink: 0,
    border: '2px solid #fe9a0044', borderTopColor: ACCENT,
    display: 'inline-block', animation: 'spin 0.8s linear infinite',
};
